import React, { useCallback, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { GridSearchIcon } from '@mui/x-data-grid';
import {
    GridColDef,
    GridFilterItem,
    GridFilterModel,
    GridLogicOperator,
    getGridDateOperators,
    getGridNumericOperators,
    getGridStringOperators,
    type GridFilterOperator,
} from '@mui/x-data-grid-pro';

const ALLOWED_OPERATORS = new Set(['contains', 'equals', 'isEmpty']);

const getTypeOperators = (type: string | undefined): GridFilterOperator[] => {
    let base: GridFilterOperator[];
    switch (type) {
        case 'date':
            base = getGridDateOperators();
            break;
        case 'number':
            base = getGridNumericOperators();
            break;
        default:
            base = getGridStringOperators();
    }
    return base.filter((op) => {
        return ALLOWED_OPERATORS.has(op.value as string);
    });
};

interface FilterCondition extends GridFilterItem {
    id: number;
}

interface CustomMultiFilterPanelProps {
    columns: GridColDef[];
    onFilterModelChange: (model: GridFilterModel) => void;
    filterState: GridFilterModel;
}

const CustomMultiFilterPanel: React.FC<CustomMultiFilterPanelProps> = ({
    columns,
    onFilterModelChange,
    filterState,
}) => {
    const transformedArray =
        filterState.items?.map((item, index) => {
            return {
                ...item,
                id: index,
            };
        }) ?? [];

    const [filterConditions, setFilterConditions] = useState<FilterCondition[]>(
        transformedArray as FilterCondition[]
    );
    const [logicOperator, setLogicOperator] = useState<GridLogicOperator>(
        filterState.logicOperator ?? GridLogicOperator.And
    );

    const handleApplyFilters = () => {
        const newFilterModel: GridFilterModel = {
            items: filterConditions.map(({ id, ...item }) => {
                return item;
            }),
            logicOperator,
        };
        onFilterModelChange(newFilterModel);
    };

    const handleAddFilter = useCallback(() => {
        setFilterConditions((prev) => {
            return [...prev, { id: Date.now(), field: '', operator: '', value: '' }];
        });
    }, []);

    const handleRemoveFilter = useCallback((id: number) => {
        setFilterConditions((prev) => {
            return prev.filter((item) => {
                return item.id !== id;
            });
        });
    }, []);

    const handleFilterChange = useCallback(
        (id: number, field: keyof GridFilterItem, value: any) => {
            setFilterConditions((prev) => {
                return prev.map((item) => {
                    return item.id === id ? { ...item, [field]: value } : item;
                });
            });
        },
        []
    );

    const getColumnType = useCallback(
        (field: string | undefined) => {
            return (
                columns.find((col) => {
                    return col.field === field;
                })?.type || 'string'
            );
        },
        [columns]
    );

    const handleOperatorList = (condition) => {
        const listData = getTypeOperators(getColumnType(condition.field));
        return listData.map((op) => {
            return (
                <MenuItem key={op.value} value={op.value}>
                    {op.value}
                </MenuItem>
            );
        });
    };

    return (
        <Box sx={{ padding: 2, border: '1px solid #ccc', borderRadius: 1, marginBottom: 2 }}>
            <Typography variant="h3" sx={{ margin: '0 0 16px 0' }}>
                Multi-Column Filter
            </Typography>

            <FormControl component="fieldset" sx={{ marginBottom: 2 }}>
                <RadioGroup
                    row
                    value={logicOperator}
                    onChange={(event) => {
                        return setLogicOperator(event.target.value as GridLogicOperator);
                    }}
                >
                    <FormControlLabel
                        value={GridLogicOperator.And}
                        control={<Radio />}
                        label="AND"
                    />
                    <FormControlLabel value={GridLogicOperator.Or} control={<Radio />} label="OR" />
                </RadioGroup>
            </FormControl>

            {filterConditions.map((condition) => {
                return (
                    <Box
                        key={condition.id}
                        sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}
                    >
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel id="column-select-label" style={{ zIndex: 100 }}>
                                Column
                            </InputLabel>
                            <Select
                                id="column-select"
                                labelId="column-select-label"
                                value={condition.field}
                                label="Column"
                                onChange={(event) => {
                                    const newField = event.target.value;
                                    handleFilterChange(condition.id, 'field', newField);
                                    handleFilterChange(condition.id, 'operator', '');
                                    handleFilterChange(condition.id, 'value', '');
                                }}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {columns.map((col) => {
                                    return (
                                        <MenuItem key={col.field} value={col.field}>
                                            {col.headerName || col.field}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 150 }} disabled={!condition.field}>
                            <InputLabel id="operator-select-label" style={{ zIndex: 100 }}>
                                Operator
                            </InputLabel>
                            <Select
                                data-testid="operator-select"
                                id="operator-select"
                                value={condition.operator}
                                label="Operator"
                                labelId="operator-select-label"
                                onChange={(event) => {
                                    return handleFilterChange(
                                        condition.id,
                                        'operator',
                                        event.target.value
                                    );
                                }}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {condition.field && handleOperatorList(condition)}
                            </Select>
                        </FormControl>

                        <Box sx={{ flexGrow: 1 }}>
                            {(() => {
                                const columnType = getColumnType(condition.field);
                                const operator = getTypeOperators(columnType).find((op) => {
                                    return op.value === condition.operator;
                                });

                                if (!operator || operator.value === 'isEmpty') {
                                    return null;
                                }

                                return (
                                    <TextField
                                        type={columnType === 'number' ? 'number' : 'text'}
                                        value={condition.value ?? ''}
                                        placeholder="Enter value"
                                        onChange={(event) => {
                                            return handleFilterChange(
                                                condition.id,
                                                'value',
                                                event.target.value
                                            );
                                        }}
                                        fullWidth
                                    />
                                );
                            })()}
                        </Box>

                        <IconButton
                            onClick={() => {
                                return handleRemoveFilter(condition.id);
                            }}
                            color="error"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                );
            })}
            <Box display={'flex'} flex={'column'} justifyContent={'space-between'}>
                <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddFilter}
                    variant="outlined"
                    sx={{ mt: 2 }}
                >
                    Add Filter Condition
                </Button>

                <Button
                    data-testid="apply-filters-button"
                    startIcon={<GridSearchIcon />}
                    onClick={handleApplyFilters}
                    variant="primary"
                    sx={{ mt: 2, marginLeft: 'auto' }}
                >
                    Apply Filter
                </Button>
            </Box>
        </Box>
    );
};

export default CustomMultiFilterPanel;
